import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { invalidateNotificationQueries } from "./invalidateNotificationQueries";
import { fcmClient, pushRegistrar } from "./index";
import { resolvePushDestination } from "./notificationRoute";
import PushInviteBanner from "./PushInviteBanner";
import { PushContext, UNSUPPORTED_STATUS } from "./pushContext";

/**
 * Mounted inside each signed-in shell. Registers this device for the session, keeps the notification
 * lists live when a push arrives, and routes a tapped push to its screen. Everything it starts is
 * undone on unmount, so signing out leaves nothing listening.
 */
export const PushNotificationsProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // The router's navigate changes identity on every location change; the listeners below must not.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [status, setStatus] = useState(UNSUPPORTED_STATUS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    pushRegistrar
      .sync()
      .then((next) => active && setStatus(next))
      .catch((error) => console.warn("Push registration failed", error));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let stopForeground;
    let stopOpened;
    let disposed = false;

    fcmClient
      .onForegroundMessage(({ title, body }) => {
        invalidateNotificationQueries(queryClient);
        // iOS already shows its own banner while the app is open.
        if (fcmClient.platform !== "IOS" && body) toast.info(title ? `${title}: ${body}` : body);
      })
      .then((stop) => (disposed ? stop() : (stopForeground = stop)))
      .catch((error) => console.warn("Could not listen for push notifications", error));

    fcmClient
      .onNotificationOpened((data) => {
        invalidateNotificationQueries(queryClient);
        const { path, state } = resolvePushDestination(data, window.location.pathname);
        navigateRef.current(path, state ? { state } : undefined);
      })
      .then((stop) => (disposed ? stop() : (stopOpened = stop)))
      .catch((error) => console.warn("Could not listen for tapped push notifications", error));

    return () => {
      disposed = true;
      stopForeground?.();
      stopOpened?.();
    };
  }, [queryClient]);

  const run = useCallback(async (action) => {
    setBusy(true);
    try {
      setStatus(await action());
    } catch (error) {
      console.warn("Push notification change failed", error);
      toast.error("Could not update push notifications. Please try again.");
    } finally {
      setBusy(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      status,
      busy,
      enable: () => run(() => pushRegistrar.enable()),
      disable: () => run(() => pushRegistrar.disable()),
      dismissInvite: () => run(() => pushRegistrar.dismissInvite()),
    }),
    [status, busy, run]
  );

  return (
    <PushContext.Provider value={value}>
      {children}
      <PushInviteBanner />
    </PushContext.Provider>
  );
};
